import type { ReactNode } from 'react'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

interface MetaIconRowProps {
  children: ReactNode
  className?: string
}

/** Horizontal row of small meta items. Children should be plain inline elements. */
export function MetaIconRow({ children, className }: MetaIconRowProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface MetaItemProps {
  icon?: ReactNode
  children: ReactNode
  className?: string
}

/** Single meta item: tiny icon + label/value, consistent gap. */
export function MetaItem({ icon, children, className }: MetaItemProps) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {icon ? <span className="inline-flex h-3.5 w-3.5 items-center justify-center text-muted-foreground/80">{icon}</span> : null}
      <span>{children}</span>
    </span>
  )
}

function humanizeActivityType(type: string): string {
  return type
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .trim()
    .replace(/^./, (c) => c.toUpperCase())
}

interface ThreadActivityBadgesProps {
  activity?: Record<string, number> | null
  /** Cap the number of badges shown. Use Infinity to show all. */
  max?: number
  size?: 'sm' | 'md'
  className?: string
}

export function ThreadActivityBadges({ activity, max = 3, size = 'sm', className }: ThreadActivityBadgesProps) {
  if (!activity) return null
  const entries = Object.entries(activity).sort((a, b) => b[1] - a[1])
  if (entries.length === 0) return null

  const visible = max === Infinity ? entries : entries.slice(0, max)
  const overflow = entries.length - visible.length

  const padding = size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-0.5'

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {visible.map(([type, count]) => (
        <Badge
          key={type}
          variant="outline"
          className={cn('text-xs font-medium tabular-nums', padding)}
        >
          <span className="text-muted-foreground">{humanizeActivityType(type)}</span>
          <span className="ml-1 text-foreground">{count}</span>
        </Badge>
      ))}
      {overflow > 0 ? (
        <span className="text-xs text-muted-foreground">+{overflow} more</span>
      ) : null}
    </div>
  )
}

type FileStatus = 'ready' | 'processing' | 'pending' | 'uploading' | 'error' | string

interface ThreadStatusPillProps {
  status: FileStatus
  className?: string
}

/** Compact status pill used in the files panel. Returns null for "ready". */
export function ThreadStatusPill({ status, className }: ThreadStatusPillProps) {
  const s = String(status || '').toLowerCase()
  if (s === 'ready' || s === 'completed' || s === 'complete' || s === 'ok') return null

  let icon: ReactNode = null
  let variant: 'secondary' | 'destructive' | 'outline' = 'secondary'
  let label = s.charAt(0).toUpperCase() + s.slice(1)

  if (s === 'processing' || s === 'uploading' || s === 'pending') {
    icon = <Loader2 className="h-3.5 w-3.5 animate-spin" />
    variant = 'secondary'
    label = s === 'pending' ? 'Pending' : s === 'uploading' ? 'Uploading' : 'Processing'
  } else if (s === 'error' || s === 'failed') {
    icon = <AlertCircle className="h-3.5 w-3.5" />
    variant = 'destructive'
    label = 'Failed'
  } else {
    icon = <CheckCircle2 className="h-3.5 w-3.5" />
    variant = 'outline'
  }

  return (
    <Badge variant={variant} className={cn('h-5 gap-1 px-1.5 text-xs font-medium', className)}>
      {icon}
      {label}
    </Badge>
  )
}
